import { ApplicationState, useAppStore } from '../store';
import { useCallback, useEffect, useRef } from 'react';

import { Config } from '../constants/Config';
import { Platform } from 'react-native';
import { LogLevel, NotificationClickEvent, OneSignal } from 'react-native-onesignal';
import { openNotificationUrl } from 'src/features/navigation/RootNavigationService';
import { useAnalytics } from './useAnalytics';
import { useGetUserId } from './useUser';

declare global {
  interface Window {
    OneSignal: any;
  }
}

if (Platform.OS === 'web') {
  window.OneSignal = window.OneSignal || [];
}

const getStringValue = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim() ? value : undefined;
};

const getNotificationUrl = (event: NotificationClickEvent): string | undefined => {
  const additionalData = event.notification.additionalData as Record<string, unknown> | undefined;

  return (
    getStringValue(event.result.url) ||
    getStringValue(event.notification.launchURL) ||
    getStringValue(additionalData?.url) ||
    getStringValue(additionalData?.link) ||
    getStringValue(additionalData?.deepLink) ||
    getStringValue(additionalData?.deeplink)
  );
};

interface UseNotifications {
  isWebPushNotificationsEnabled: () => Promise<boolean>;
  isPushNotificationsEnabled: () => Promise<boolean>;
  setWebPushExternalUserId: () => Promise<void>;
  setExternalUserId: () => Promise<void>;
  removeWebPushExternalUserId: () => Promise<void>;
  removeExternalUserId: () => Promise<void>;
  isWebPushNotificationsSupported: () => Promise<boolean>;
  isPushNotificationsSupported: () => Promise<boolean>;
  setWebPushSubscription: (unmute: boolean) => Promise<boolean>;
  setSubscription: (unmute: boolean) => Promise<boolean>;
  initializeOneSignalWeb: () => Promise<void>;
  initializeOneSignal: () => Promise<void>;
}

export const useNotifications = (): UseNotifications => {
  const userId = useGetUserId();
  const { addAnalyticsLog } = useAnalytics('useOneSignal.ts');

  const isWebPushNotificationsEnabled = useCallback(async (): Promise<boolean> => {
    try {
      if (Platform.OS === 'web') {
        return new Promise((resolve) => {
          window.OneSignal.push(() => {
            window.OneSignal.isPushNotificationsEnabled((isEnabled: boolean) => {
              resolve(isEnabled);
            });
          });
        });
      }
      return false;
    } catch (error) {
      addAnalyticsLog({ function: 'isWebPushNotificationsEnabled', data: error, logType: 'error' });
      return false;
    }
  }, [addAnalyticsLog]);

  const isPushNotificationsEnabled = async (): Promise<boolean> => {
    if (Platform.OS === 'web') {
      return isWebPushNotificationsEnabled();
    } else {
      try {
        // Correct way to check notification permission in 5.2.9
        const hasPermission = await OneSignal.Notifications.getPermissionAsync();
        if (hasPermission) {
          return await OneSignal.User?.pushSubscription?.getOptedInAsync();
        }
        return false;
      } catch (error) {
        addAnalyticsLog({ function: 'isPushNotificationsEnabled', data: error, logType: 'error' });
        return false;
      }
    }
  };

  const setWebPushExternalUserId = useCallback(async (): Promise<void> => {
    try {
      const enabled = await isWebPushNotificationsEnabled();
      if (enabled && userId) {
        window.OneSignal.push(() => {
          window.OneSignal.setExternalUserId(userId);
        });
      }
    } catch (error) {
      addAnalyticsLog({ function: 'setWebPushExternalUserId', data: error, logType: 'error' });
    }
  }, [addAnalyticsLog, isWebPushNotificationsEnabled, userId]);

  const setExternalUserId = async (): Promise<void> => {
    if (Platform.OS === 'web') {
      await setWebPushExternalUserId();
    } else {
      try {
        if (userId) {
          OneSignal.login(userId);
        }
      } catch (error) {
        addAnalyticsLog({ function: 'setExternalUserId', data: error, logType: 'error' });
      }
    }
  };

  const removeWebPushExternalUserId = useCallback(async (): Promise<void> => {
    try {
      window.OneSignal.push(() => {
        window.OneSignal.removeExternalUserId();
      });
    } catch (error) {
      addAnalyticsLog({ function: 'removeWebPushExternalUserId', data: error, logType: 'error' });
    }
  }, [addAnalyticsLog]);

  const removeExternalUserId = useCallback(async (): Promise<void> => {
    if (Platform.OS === 'web') {
      await removeWebPushExternalUserId();
    } else {
      try {
        OneSignal.logout();
      } catch (error) {
        addAnalyticsLog({ function: 'removeExternalUserId', data: error, logType: 'error' });
      }
    }
  }, [addAnalyticsLog, removeWebPushExternalUserId]);

  const isWebPushNotificationsSupported = async (): Promise<boolean> => {
    try {
      if (Platform.OS === 'web') {
        return new Promise((resolve) => {
          window.OneSignal.push(() => {
            window.OneSignal.isPushNotificationsSupported((supported: boolean) => {
              resolve(supported);
            });
          });
        });
      }
      return false;
    } catch (error) {
      addAnalyticsLog({ function: 'isWebPushNotificationsSupported', data: error, logType: 'error' });
      return false;
    }
  };

  const isPushNotificationsSupported = async (): Promise<boolean> => {
    if (Platform.OS === 'web') {
      return isWebPushNotificationsSupported();
    }

    // default all mobile devices support notitfication
    return true;
  };

  const setWebPushSubscription = async (unmute: boolean): Promise<boolean> => {
    try {
      window.OneSignal.push(() => {
        window.OneSignal.setSubscription(unmute);
      });
      return isWebPushNotificationsEnabled();
    } catch (error) {
      addAnalyticsLog({ function: 'setWebPushSubscription', data: error, logType: 'error' });
      return false;
    }
  };

  const setSubscription = async (enable: boolean): Promise<boolean> => {
    if (Platform.OS === 'web') {
      return setWebPushSubscription(enable);
    } else {
      try {
        if (enable === true) {
          const hasPermission = await OneSignal.Notifications.requestPermission(true);
          if (!hasPermission) {
            return false;
          }
          OneSignal.User?.pushSubscription?.optIn();
        } else {
          OneSignal.User?.pushSubscription?.optOut();
        }

        return isPushNotificationsEnabled();
      } catch (error) {
        addAnalyticsLog({ function: 'setSubscription', data: error, logType: 'error' });
        return false;
      }
    }
  };

  const initializeOneSignalWeb = useCallback(async (): Promise<void> => {
    if (Platform.OS === 'web') {
      window.OneSignal = window.OneSignal || [];
      window.OneSignal.push(() => {
        window.OneSignal.init({ appId: Config.ONESIGNAL_APP_ID });
      });
    }
  }, []);

  const initializeOneSignal = useCallback(async (): Promise<void> => {
    if (Platform.OS === 'web') {
      await initializeOneSignalWeb();
    } else {
      try {
        OneSignal.Debug.setLogLevel(LogLevel.Verbose);
        OneSignal.initialize(Config.ONESIGNAL_APP_ID);
        OneSignal.Notifications.addEventListener('click', (event) => {
          openNotificationUrl(getNotificationUrl(event));
        });
        await OneSignal.Notifications.requestPermission(false);

        if (userId) {
          OneSignal.login(userId);
        }
      } catch (error) {
        addAnalyticsLog({
          function: 'initializeOneSignal',
          data: error,
          logType: 'error'
        });
      }
    }
  }, [addAnalyticsLog, initializeOneSignalWeb, userId]);

  return {
    isWebPushNotificationsEnabled,
    isPushNotificationsEnabled,
    setWebPushExternalUserId,
    setExternalUserId,
    removeWebPushExternalUserId,
    removeExternalUserId,
    isWebPushNotificationsSupported,
    isPushNotificationsSupported,
    setWebPushSubscription,
    setSubscription,
    initializeOneSignalWeb,
    initializeOneSignal
  };
};

export const useInitNotifications = () => {
  const { initializeOneSignal, removeExternalUserId } = useNotifications();
  const isAppInitComplete = useAppStore((state: ApplicationState) => state.isAppInitComplete);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isAppInitComplete && !isInitialized.current) {
      initializeOneSignal().then(() => {
        isInitialized.current = true;
      });
    }

    return () => {
      if (isInitialized.current) {
        removeExternalUserId().catch((error) => {
          console.warn('Failed to remove external ID:', error);
        });
      }
    };
  }, [initializeOneSignal, isAppInitComplete, removeExternalUserId]);
};
